import { useId, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { addDays, differenceInCalendarDays, format, isValid, parseISO, startOfMonth, startOfToday } from 'date-fns'
import { CalendarDays } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Calendar } from '@/shared/components/ui/calendar'
import { FrenchDateInput } from '@/shared/components/cse/FrenchDateInput'
import { Label } from '@/shared/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { dateFnsLocale } from '@/shared/lib/datetime'
import { cn } from '@/shared/lib/utils'

const FLEX_OPTIONS = [0, 1, 2, 3, 7] as const

export interface DateRangePickerProps {
  /** ISO `yyyy-MM-dd` ou null. */
  from: string | null
  to: string | null
  onChange: (range: { from: string | null; to: string | null }) => void
  /** Tolérance de recherche ±N jours (0 = exact). Affiche les chips si `onFlexChange` fourni. */
  flexDays?: number
  onFlexChange?: (days: number) => void
  /** Mode bloc : séjours verrouillés de N nuits (clic début → fin = début+N, verrouillée). */
  lockedNights?: number | null
  className?: string
}

function toDate(value: string | null): Date | undefined {
  if (value === null || value === '') return undefined
  const d = parseISO(value)
  return isValid(d) ? d : undefined
}

function iso(date: Date | undefined): string | null {
  return date ? format(date, 'yyyy-MM-dd') : null
}

/**
 * Sélecteur de plage de dates V2 (RV-22) - react-day-picker + Popover.
 * Desktop : bouton → calendrier 2 mois ; mobile (`<md`) : repli sur 2 champs `date` natifs.
 * Modes : plage libre, ou **bloc verrouillé** de `lockedNights` nuits. Bloque le passé,
 * affiche le nombre de nuits, et propose une **souplesse ±N jours** (tolérance de recherche).
 */
export function DateRangePicker({
  from,
  to,
  onChange,
  flexDays,
  onFlexChange,
  lockedNights,
  className,
}: DateRangePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const fromId = useId()
  const toId = useId()
  const today = startOfToday()
  const minMonth = startOfMonth(today)

  const fromDate = toDate(from)
  const toEndDate = toDate(to)
  const isBlock = typeof lockedNights === 'number' && lockedNights > 0
  const nights = fromDate && toEndDate ? differenceInCalendarDays(toEndDate, fromDate) : 0

  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(fromDate ?? today))

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      setVisibleMonth(startOfMonth(fromDate ?? today))
    }
    setOpen(next)
  }

  const clampMonth = (month: Date): Date => {
    const normalized = startOfMonth(month)
    return normalized < minMonth ? minMonth : normalized
  }

  const calendarLabels = {
    labelPrevious: () => t('reservation.properties.calendar.prev_month'),
    labelNext: () => t('reservation.properties.calendar.next_month_nav'),
  }

  const sharedCalendarProps = {
    numberOfMonths: 2 as const,
    showOutsideDays: false as const,
    disabled: { before: today },
    startMonth: minMonth,
    month: visibleMonth,
    onMonthChange: (month: Date) => setVisibleMonth(clampMonth(month)),
    labels: calendarLabels,
  }

  const triggerLabel =
    fromDate && toEndDate
      ? `${format(fromDate, 'd MMM', { locale: dateFnsLocale() })} – ${format(toEndDate, 'd MMM', { locale: dateFnsLocale() })}`
      : fromDate
        ? `${format(fromDate, 'd MMM', { locale: dateFnsLocale() })} – …`
        : t('reservation.properties.date_picker.placeholder')

  const selectFrom = (day: Date): void => {
    onChange({ from: iso(day), to: iso(addDays(day, lockedNights ?? 0)) })
  }

  return (
    <div className={cn('flex h-full min-w-0 flex-col', className)}>
      <Label className="mb-1.5">{t('reservation.properties.date_picker.label')}</Label>

      {/* Desktop : popover calendrier */}
      <div className="mt-auto hidden md:block">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger
            className={cn(
              'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm',
              'outline-none ring-offset-background transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className={cn('truncate', !fromDate && 'text-muted-foreground')}>{triggerLabel}</span>
            {nights > 0 ? (
              <span className="ml-auto shrink-0 rounded-pill bg-[var(--v2-brand-soft)] px-2 py-0.5 text-xs font-medium text-primary">
                {t('reservation.properties.date_picker.nights', { count: nights })}
              </span>
            ) : null}
          </PopoverTrigger>
          <PopoverContent align="start" className="p-2">
            {isBlock ? (
              <Calendar
                mode="single"
                selected={fromDate}
                onSelect={(day) => {
                  if (day) selectFrom(day)
                }}
                {...sharedCalendarProps}
                modifiers={{ stay: fromDate && toEndDate ? { from: fromDate, to: toEndDate } : false }}
                modifiersStyles={{ stay: { backgroundColor: 'var(--v2-brand-soft)' } }}
              />
            ) : (
              <Calendar
                mode="range"
                selected={fromDate ? ({ from: fromDate, to: toEndDate } as DateRange) : undefined}
                onSelect={(range) => onChange({ from: iso(range?.from), to: iso(range?.to) })}
                {...sharedCalendarProps}
              />
            )}

            {isBlock && fromDate && toEndDate ? (
              <p className="px-2 pt-1 text-xs text-muted-foreground">
                {t('reservation.properties.date_picker.block_hint', {
                  count: lockedNights ?? 0,
                  date: format(toEndDate, 'd MMMM', { locale: dateFnsLocale() }),
                })}
              </p>
            ) : null}

            <div className="flex items-center justify-between gap-2 px-2 pt-2">
              {onFlexChange ? (
                <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t('reservation.properties.date_picker.flex_label')}>
                  {FLEX_OPTIONS.map((opt) => {
                    const active = (flexDays ?? 0) === opt
                    return (
                      <button
                        key={opt}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onFlexChange(opt)}
                        className={cn(
                          'rounded-pill border px-2.5 py-1 text-xs font-medium transition-colors',
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border text-muted-foreground hover:bg-accent/40',
                        )}
                      >
                        {opt === 0
                          ? t('reservation.properties.date_picker.flex_exact')
                          : t('reservation.properties.date_picker.flex_n', { count: opt })}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => onChange({ from: null, to: null })}
                className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                {t('reservation.properties.date_picker.clear')}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile : champs date FR (jj/mm/aaaa) */}
      <div className="mt-auto grid grid-cols-2 gap-2 md:hidden">
        <div className="flex flex-col">
          <Label htmlFor={fromId} className="sr-only">
            {t('reservation.properties.filters.available_from')}
          </Label>
          <FrenchDateInput
            id={fromId}
            min={format(today, 'yyyy-MM-dd')}
            value={from ?? ''}
            onChange={(v) => {
              onChange({ from: v || null, to: isBlock && v ? iso(addDays(parseISO(v), lockedNights ?? 0)) : to })
            }}
          />
        </div>
        <div className="flex flex-col">
          <Label htmlFor={toId} className="sr-only">
            {t('reservation.properties.filters.available_to')}
          </Label>
          <FrenchDateInput
            id={toId}
            min={from ?? format(today, 'yyyy-MM-dd')}
            value={to ?? ''}
            disabled={isBlock}
            onChange={(v) => onChange({ from, to: v || null })}
          />
        </div>
      </div>
    </div>
  )
}
